/**
 * JobDescriptionsListPage -- Lists all saved parsed job descriptions.
 *
 * Uses useJobDescriptionStore (ADR-021), List + LoadingState (ADR-022).
 * Clicking a row navigates to the parser page with the selected JD loaded.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useJobDescriptionStore,
  useJobDescriptionActions,
} from '../../state/cms/useJobDescriptionStore';
import { List } from '../../components/ui/List';
import { LoadingState } from '../../components/ui/LoadingState';
import { Button } from '../../components/ui/Button';
import { logger } from '../../services/logger.client';
import type { JobDescription } from '../../types/job-description.types';

const log = logger.child({ module: 'JobDescriptionsListPage' });

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: string }) {
  const variant =
    status === 'parsed'
      ? 'badge--published'
      : status === 'failed'
        ? 'badge--danger'
        : status === 'parsing'
          ? 'badge--warning'
          : 'badge--outline';

  return <span className={`badge ${variant}`}>{status}</span>;
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export function JobDescriptionsListPage() {
  const navigate = useNavigate();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const jobDescriptions = useJobDescriptionStore((s) => s.jobDescriptions);
  const isLoading = useJobDescriptionStore((s) => s.isLoading);
  const error = useJobDescriptionStore((s) => s.error);
  const { fetchJobDescriptions, deleteJobDescription, clearSelected } =
    useJobDescriptionActions();

  useEffect(() => {
    log.info('Job descriptions list page mounted -- fetching');
    void fetchJobDescriptions();
  }, [fetchJobDescriptions]);

  function handleRowClick(jd: JobDescription) {
    /* Load the selected JD in the store then navigate to the parser page
       so the user can see the parsed data and create a site from it. */
    clearSelected();
    useJobDescriptionStore.setState((state) => {
      state.selectedJD = jd;
    });
    navigate('/cms/tools/parse-job');
  }

  async function handleDelete(id: string) {
    await deleteJobDescription(id);
    setConfirmDeleteId(null);
  }

  return (
    <div className="cms-page">
      <header className="cms-page__header">
        <h1 className="cms-page__title">Saved Job Descriptions</h1>
        <Button
          variant="primary"
          size="md"
          onClick={() => {
            clearSelected();
            navigate('/cms/tools/parse-job');
          }}
        >
          Parse New
        </Button>
      </header>

      <LoadingState loading={isLoading} error={error}>
        <List<JobDescription>
          items={jobDescriptions}
          keyExtractor={(jd) => jd.id}
          emptyMessage="No saved job descriptions yet. Click 'Parse New' to get started."
          className="cms-site-list"
        >
          {(jd) => (
            <li className="cms-site-card" onClick={() => handleRowClick(jd)}>
              <div className="cms-site-card__header">
                <h3 className="cms-site-card__name">
                  {jd.job_title ?? 'Untitled Job'}
                </h3>
                <StatusBadge status={jd.status} />
              </div>
              {jd.company_name && (
                <p className="cms-site-card__domain">{jd.company_name}</p>
              )}
              <div className="cms-site-card__meta">
                <span>{formatDate(jd.created_at)}</span>
                {jd.source_type === 'url' && jd.source_url && (
                  <span title={jd.source_url}> -- from URL</span>
                )}
              </div>
              <div
                className="cms-site-card__actions"
                onClick={(e) => e.stopPropagation()}
              >
                {confirmDeleteId === jd.id ? (
                  <>
                    <span className="jd-list__confirm-text">Delete?</span>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => void handleDelete(jd.id)}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setConfirmDeleteId(null)}
                    >
                      No
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setConfirmDeleteId(jd.id)}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </li>
          )}
        </List>
      </LoadingState>
    </div>
  );
}
